import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/baseUi/carousel"
import { Text } from "@/components/ui/baseUi/text"
import { useCurrentBoxId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { FavoritePersonalRecordEntity } from "@modules/profile/publicProfile/entities/favoritesPersonalRecord"
import { PublicProfileEntity } from "@modules/profile/publicProfile/entities/publicProfileEntity"
import { ProgramBundleItemEntity } from "@modules/programBundle/entities/programBundleEntity"
import { UserProgramItemEntity } from "@modules/programs/entities/userProgramItemEntity"
import { HuProgramMiniCards } from "@modules/ui/components/cards/huProgramMiniCard"
import { HuBioCard } from "@modules/ui/components/huBioCard"
import { HUFavoritePersonalRecordMiniCards } from "@modules/ui/components/huFavoritePersonalRecordMiniCards"
import dayjs from "dayjs"
import { Dumbbell } from "lucide-react"

type Props = {
  publicProfile: PublicProfileEntity
  isBioCollapsible?: boolean
  isBioCollapsed?: boolean
  className?: string
} & React.ComponentProps<"div">

export function PublicProfileDetails({
  publicProfile,
  isBioCollapsible = false,
  isBioCollapsed = false,
  className,
  ...props
}: Props) {
  const boxId = useCurrentBoxId()

  const programCardCarrouselTemplate = (program: UserProgramItemEntity | ProgramBundleItemEntity) => {
    return <HuProgramMiniCards item={program} disabled={!!boxId} />
  }

  const personalRecordCarrouselTemplate = (personalRecord: FavoritePersonalRecordEntity) => {
    return <HUFavoritePersonalRecordMiniCards item={personalRecord} />
  }

  return (
    <div className={cn("flex w-full flex-col pb-4", className)} {...props}>
      {/* BIO */}
      <HuBioCard
        description={publicProfile.biography}
        age={dayjs().diff(publicProfile.birthDate, "year")}
        weight={publicProfile.weight}
        height={publicProfile.height}
        category={publicProfile.genderCategory}
        job={publicProfile.job}
        style={{
          marginTop: isBioCollapsible ? -50 : 0,
          marginBottom: 20,
          padding: isBioCollapsible ? (isBioCollapsed ? "0px 2rem" : "70px 2rem 2rem 2rem") : "2rem",
          maxHeight: isBioCollapsed ? "0px" : "1000px",
          transition: "all 0.5s ease-in-out",
        }}
      />
      <div
        className={cn(
          "flex w-full flex-col gap-4 px-5 transition-all duration-500 ease-in-out",
          isBioCollapsed ? "pt-12" : "pt-0",
        )}
      >
        {/* CROSSFIT START DATE */}
        {publicProfile.crossfitStartDate && (
          <div className="flex flex-row gap-2">
            <Dumbbell strokeWidth="1" />
            <div className="mt-1 flex flex-col gap-1">
              <Text className="font-bold text-textDefault-caption uppercase" fontStyle="BS">
                {word("publicProfile.startDate")}
              </Text>
              <Text fontStyle="TS">{dayjs(publicProfile.crossfitStartDate).format("L")}</Text>
            </div>
          </div>
        )}

        {/* BADGES */}
        {publicProfile.badges && publicProfile.badges.length > 0 && (
          <div className="flex flex-col gap-2">
            <Text fontStyle="TM">{word("publicProfile.badges")}</Text>
            <div className="flex flex-wrap gap-2">
              {publicProfile.badges.map((badge, index) => (
                <img key={index} src={badge.badge.image.url} alt={badge.badge.name} />
              ))}
            </div>
          </div>
        )}

        {/* PERSONAL RECORDS */}
        {publicProfile.favoritesPersonalRecords.length > 0 && (
          <div className="flex flex-col gap-2">
            <Text fontStyle="TM">{word("publicProfile.personalRecords")}</Text>
            <div className="flex gap-2">
              <Carousel className="mx-auto w-8/10" opts={{ align: "start", loop: true }}>
                <CarouselContent>
                  {publicProfile.favoritesPersonalRecords.map((program, index) => (
                    <CarouselItem className="basis-34" key={index}>
                      {personalRecordCarrouselTemplate(program)}
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            </div>
          </div>
        )}

        {/* PROGRAMS */}
        {publicProfile.ownedPrograms && publicProfile.ownedPrograms.length > 0 && (
          <div className="flex flex-col gap-2">
            <Text fontStyle="TM">{word("publicProfile.userPersonalPrograms")}</Text>
            <div className="flex gap-2">
              <Carousel className="mx-auto w-8/10" opts={{ align: "start", loop: true }}>
                <CarouselContent>
                  {publicProfile.ownedPrograms.map((program, index) => (
                    <CarouselItem className="basis-44" key={index}>
                      {programCardCarrouselTemplate(program)}
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            </div>
          </div>
        )}
        {/* FOLLOWED PROGRAMS */}
        {publicProfile.followedPrograms && publicProfile.followedPrograms.length > 0 && (
          <div className="flex flex-col gap-2">
            <Text fontStyle="TM">{word("publicProfile.followedPrograms")}</Text>
            <div className="flex gap-2">
              <Carousel className="mx-auto w-8/10" opts={{ align: "start", loop: true }}>
                <CarouselContent>
                  {publicProfile.followedPrograms.map((program, index) => (
                    <CarouselItem className="basis-44" key={index}>
                      {programCardCarrouselTemplate(program)}
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
